import { api } from './client'
import type { PipelineDetail, PipelineSummary, Section, Stage, StageType } from './types'

/** Mirrors backend PipelineRequest. */
export interface PipelineInput {
  name: string
  isDefault?: boolean
}

/** Mirrors backend SectionRequest. */
export interface SectionInput {
  name: string
  position?: number | null
  /** Background tint (hex, e.g. #E0F2FE) for the section's group on the board; null clears it. */
  color?: string | null
}

/** Mirrors backend StageRequest — PUT replaces the whole stage, so always send every field. */
export interface StageInput {
  sectionId?: string | null
  name: string
  shortName?: string | null
  color?: string | null
  position?: number | null
  stageType?: StageType
  autoAdvance?: boolean
  advanceAfterDays?: number | null
  advanceOnNextContact?: boolean
  requiredFields?: string[] | null
}

export async function listPipelines(): Promise<PipelineSummary[]> {
  const { data } = await api.get<PipelineSummary[]>('/api/pipelines')
  return data
}

export async function getPipeline(id: string): Promise<PipelineDetail> {
  const { data } = await api.get<PipelineDetail>(`/api/pipelines/${id}`)
  return data
}

export async function createPipeline(input: PipelineInput): Promise<PipelineSummary> {
  const { data } = await api.post<PipelineSummary>('/api/pipelines', input)
  return data
}

export async function updatePipeline(id: string, input: PipelineInput): Promise<PipelineSummary> {
  const { data } = await api.put<PipelineSummary>(`/api/pipelines/${id}`, input)
  return data
}

/** Sections and stages are removed with the pipeline; a pipeline that still has deals fails with 409. */
export async function deletePipeline(id: string): Promise<void> {
  await api.delete(`/api/pipelines/${id}`)
}

// --- sections ---

export async function createSection(pipelineId: string, input: SectionInput): Promise<Section> {
  const { data } = await api.post<Section>(`/api/pipelines/${pipelineId}/sections`, input)
  return data
}

export async function updateSection(id: string, input: SectionInput): Promise<Section> {
  const { data } = await api.put<Section>(`/api/sections/${id}`, input)
  return data
}

/** Deleting a section keeps its stages — they become unsectioned (DB FK sets sectionId to null). */
export async function deleteSection(id: string): Promise<void> {
  await api.delete(`/api/sections/${id}`)
}

// --- stages ---

export async function createStage(pipelineId: string, input: StageInput): Promise<Stage> {
  const { data } = await api.post<Stage>(`/api/pipelines/${pipelineId}/stages`, input)
  return data
}

export async function updateStage(id: string, input: StageInput): Promise<Stage> {
  const { data } = await api.put<Stage>(`/api/stages/${id}`, input)
  return data
}

export async function deleteStage(id: string): Promise<void> {
  await api.delete(`/api/stages/${id}`)
}
